import React from "react";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Box, Typography } from "@mui/material";
import { useScreenshotTranslation } from "../../../lib/screenshotI18n";
import {
    giantBottomOverlaySx,
    giantGlowPillSx,
    giantTopOverlaySx,
    metaPillHardSx,
    metaPillSx,
    metaPillsRowSx,
    metaRegionCompactSx,
    metaRegionOverlaySx,
    promoHeadlineLightSx,
} from "../styles/screenshotStyles";

/**
 * Bottom marketing strip — piece count + difficulty pills (App Store screenshot).
 * `overlay` — floating capsule(s) over full-bleed artwork (shot 04).
 * `singlePiecesPill` — one charcoal “1024 Pieces” shield pill only.
 */
export default function ScreenshotPiecesDifficultyBar({
    piecesLabel,
    difficultyLabel,
    hard = false,
    overlay = false,
    singlePiecesPill = false,
    headline = null,
    compact = false,
}) {
    const { t } = useScreenshotTranslation();
    const piecesText =
        piecesLabel != null
            ? (() => {
                  const raw = t("mobileScreenshot.jigsaw.meta-pieces", { count: piecesLabel });
                  return raw === "mobileScreenshot.jigsaw.meta-pieces"
                      ? `${piecesLabel} Pieces`
                      : raw;
              })()
            : null;
    const difficultyKey = String(difficultyLabel ?? "")
        .toLowerCase()
        .replace(/\s+/g, "-");
    const difficulty =
        difficultyLabel != null
            ? (() => {
                  const key = `mobileScreenshot.jigsaw.difficulty-${difficultyKey}`;
                  const raw = t(key);
                  return raw === key ? String(difficultyLabel) : raw;
              })()
            : null;

    if (!piecesText && !difficulty && !overlay) return null;

    if (overlay) {
        return (
            <>
                {headline ? (
                    <Box sx={giantTopOverlaySx}>
                        <Typography
                            component="h1"
                            sx={{
                                ...promoHeadlineLightSx,
                                m: 0,
                                fontWeight: 800,
                                fontSize: { xs: "1.85rem", sm: "2.15rem" },
                                letterSpacing: "-0.02em",
                                textShadow: "0 2px 16px rgba(0,0,0,0.55)",
                            }}
                        >
                            {headline}
                        </Typography>
                    </Box>
                ) : null}
                <Box sx={giantBottomOverlaySx}>
                    {singlePiecesPill && piecesText ? (
                        <Box sx={{ ...giantGlowPillSx, pointerEvents: "auto" }}>
                            <ShieldOutlinedIcon
                                sx={{ fontSize: 20, color: "rgba(255,255,255,0.92)" }}
                                aria-hidden
                            />
                            <Typography component="span" sx={{ fontWeight: 800, fontSize: "0.98rem" }}>
                                {piecesText}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ ...metaPillsRowSx, gap: 1.5, pointerEvents: "auto" }}>
                            {piecesText ? (
                                <Box sx={giantGlowPillSx}>
                                    <ExtensionOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />
                                    <Typography component="span" sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                                        {piecesText}
                                    </Typography>
                                </Box>
                            ) : null}
                            {difficulty ? (
                                <Box sx={giantGlowPillSx}>
                                    <ShieldOutlinedIcon sx={{ fontSize: 22, color: "#f87171" }} aria-hidden />
                                    <Typography component="span" sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                                        {difficulty}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    )}
                </Box>
            </>
        );
    }

    const pillSx = hard ? metaPillHardSx : metaPillSx;

    return (
        <Box sx={compact ? metaRegionCompactSx : metaRegionOverlaySx}>
            <Box sx={metaPillsRowSx}>
                {piecesText ? (
                    <Box sx={pillSx}>
                        <ExtensionOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />
                        <Typography component="span" sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                            {piecesText}
                        </Typography>
                    </Box>
                ) : null}
                {difficulty ? (
                    <Box sx={pillSx}>
                        <ShieldOutlinedIcon className="meta-pill-shield" sx={{ fontSize: 22 }} aria-hidden />
                        <Typography component="span" sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                            {difficulty}
                        </Typography>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}
